/** @format */

import { memo } from 'react'
import { CopyBlock, shadesOfPurple } from 'react-code-blocks'

import styles from 'dir/styles/courses.module.css'
import timesNewRomanImg from 'assets/images/timesNewRoman.png'

export const _1 = memo(() => (
  <section className={styles.section}>
    <h2 className={styles.h2}>1 - Работа со шрифтами</h2>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>1.1 - Семейства шрифтов</h3>
      <p className={styles.description}>
        Шрифты собраны в семейства. По умолчанию, если никакой другой шрифт не задан, браузер использует шрифт из
        семейства sans - "Times New Roman".
      </p>
      <img className={styles.img_without_shadow} src={timesNewRomanImg} alt="Times New Roman" />
      <p className={styles.notificationWarn}>
        Даже если вы приобретаете шрифт для личного использования, то такой шрифт нельзя применять на веб-странице, т.к.
        любой пользователь может скачать себе этот шрифт со страницы бесплатно. Если же разработчик подключил шрифт, и
        не убедился, что лицензия позволяет этот шрифт использовать для публикации в сети, то вся ответственность за
        неправовое использование шрифта, ложиться на разработчика. Поэтому в сомнительных случаях лучше подобрать шрифт
        со схожим начертанием из заведомо законных источников, например сервиса{' '}
        <a className={styles.link} target="_blank" rel="noopener noreferrer" href="https://fonts.google.com/">
          Google Fonts
        </a>
        .
      </p>
      <section className={styles.subsection}>
        <h4 className={styles.h4}>1.3.1 - Поиск шрифта и подключение к селектору</h4>
        <p className={styles.description}>
          Для того чтобы некоторому селектору задать шрифт или семейство шрифтов применяют CSS-свойство{' '}
          <code className={styles.code}>font-family</code>. Синтаксис свойства позволяет указать через запятую не один
          шрифт, а перечислить несколько шрифтов и задать семейство шрифтов. При этом отображаться будет первый шрифт,
          который браузер сможет получить от ОС. Порядок поиска соответствует потоку выполнения - браузер будет
          считывать слева направо содержимое перечисления.
        </p>
        <p className={styles.description}>Рассмотрим следующую запись:</p>
        <CopyBlock
          text={`element {
  font-family: Roboto, Verdana, sans-serif;
}
`}
          language="html"
          showLineNumbers
          theme={shadesOfPurple}
        />
        <p className={styles.description}>
          Браузер начнет искать <code className={styles.code}>Roboto</code> и в случае неудачи перейдет к{' '}
          <code className={styles.code}>Verdana</code>. Если отсутствует <code className={styles.code}>Verdana</code>,
          поток выполнения доберется до <code className={styles.code}>sans-serif</code> и операционная система вернет
          шрифт по умолчанию.
        </p>
      </section>
      <section className={styles.subsection}>
        <h4 className={styles.h4}>1.3.2 - Подключение шрифта, отсутствующего в ОС</h4>
        <p className={styles.description}>
          Пользователям пришлось бы устанавливать шрифты в систему каждый раз, когда разработчик сайта применил
          несистемный шрифт. Избавить от этой проблемы помогут <b>директивы</b> CSS{' '}
          <code className={styles.code}>@font-face</code> или <code className={styles.code}>@import</code>, также можно
          использовать HTML тег подключения внешних ресурсов <code className={styles.code}>link</code>. С помощью этих
          средств браузер имеет возможность подключать шрифт независимо от ОС из внешнего источника: файла или сетевого
          ресурса.{' '}
        </p>
        <p className={styles.description}>Рассмотрим пример подключения.</p>
        <CopyBlock
          text={`p {
  font-family: "Web font", Helvetica, Tahoma, sans-serif;
}

@font-face {
  font-family: 'Web font';
  src: url('webfont.woff2') format('woff2'),
       url('webfont.ttf')  format('truetype'), /* Только если нужна поддержка старых Android, иначе закомментировать */
       url('webfont.woff') format('woff');
  font-weight: normal;
  font-style: normal;
}
`}
          language="html"
          showLineNumbers
          theme={shadesOfPurple}
        />
      </section>
    </section>
    <section className={styles.subsection}>
      <h3 className={styles.h3}>
        1.4 - Как подключить несколько файлов одного шрифта и использовать одно название шрифта
      </h3>
      <p className={styles.description}>
        Довольно часто, веб-шрифт это несколько файлов: один файл — одно начертание (файл_1 — плотность 400 нормальный;
        файл_2 — плотность 400 италик, и т. д.). А хочется везде при указании шрифта использовать одно имя, без
        упоминания в слов normal, italic и т.п., а указывать это в соответствующих CSS-свойствах. Подключаем несколько
        файлов шрифта с указанием одного и того же имени, но разных <code className={styles.code}>font-weight</code> и{' '}
        <code className={styles.code}>font-style</code>.{' '}
      </p>
      <CopyBlock
        text={`@font-face {
  font-family: 'Web font';       /* название одно */
  src: url('webfont8321.woff2'); /* разные файлы */
  font-weight: 400;              /* разный вес/жирность */
  font-style: normal;
}

@font-face {
  font-family: 'Web font';       /* название одно */
  src: url('webfont___1.woff2'); /* разные файлы */
  font-weight: 700;              /* разный вес/жирность */
  font-style: normal;
}
`}
        language="html"
        showLineNumbers
        theme={shadesOfPurple}
      />
    </section>
  </section>
))
